import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Gift, ArrowRight, Sparkles } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const PLAN_BENEFITS = {
  growth: ["Fundraising CRM + round modeling", "Tracked deck links", "Cap table + 10 equity pages", "Up to 150 investors", "3 team seats"],
  scale: ["Cap table + 100 employee equity pages", "Data rooms with view tracking", "Document Builder + e-signatures", "Up to 500 investors", "10 team seats"],
  fund_manager: ["Everything in Scale", "Unlimited funds & SPVs", "LP portal, capital calls & waterfalls", "Up to 1,000 investors & LPs", "25 team seats"],
};

export default function RedeemAccess() {
  const { code: routeCode } = useParams();
  const { user, reload, loading } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState(routeCode ? routeCode.toUpperCase() : "");
  const [info, setInfo] = useState(null); // { valid, status, plan, plan_name }
  const [checking, setChecking] = useState(!!routeCode);
  const [busy, setBusy] = useState(false);

  const isFounder = user?.role === "founder";

  const check = async (c) => {
    setChecking(true); setInfo(null);
    try {
      const { data } = await api.get(`/access-codes/${encodeURIComponent(c)}/check`);
      setInfo(data);
    } catch { setInfo({ valid: false, status: "invalid" }); }
    finally { setChecking(false); }
  };

  useEffect(() => { if (routeCode) check(routeCode.toUpperCase()); }, [routeCode]);
  // Once an authed founder is here, the code carried across signup has served its purpose.
  useEffect(() => { if (isFounder) sessionStorage.removeItem("tenx_redeem_code"); }, [isFounder]);

  const redeem = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/access-codes/redeem", { code: code.trim() });
      toast.success(`You're on ${data.plan_name} - free, forever`);
      await reload();
      navigate("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem that code");
      setBusy(false);
    }
  };

  const goAuth = (path) => {
    sessionStorage.setItem("tenx_redeem_code", code.trim());
    navigate(path);
  };

  const benefits = PLAN_BENEFITS[info?.plan] || PLAN_BENEFITS.scale;

  return (
    <div className="flex min-h-screen flex-col justify-center bg-background px-5 py-10" data-testid="redeem-page">
      <div className="mx-auto w-full max-w-md">
        <div className="flex justify-center"><Logo /></div>
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
          <div className="flex items-center justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary"><Gift className="h-6 w-6 text-primary-foreground" /></div>
          </div>
          <h1 className="ff-cabinet mt-4 text-center text-2xl font-bold tracking-tight text-slate-900">Claim your free access</h1>
          <p className="ff-manrope mt-1 text-center text-sm text-slate-500">Redeem your TenX access code to unlock a paid plan, on the house.</p>

          {!routeCode && (
            <form onSubmit={(e) => { e.preventDefault(); if (code.trim()) check(code.trim()); }} className="mt-5 flex gap-2" data-testid="redeem-manual-form">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="TENX-XXXX-XXXX" className="h-11 font-mono uppercase" data-testid="redeem-manual-input" />
              <Button type="submit" disabled={checking || !code.trim()} className="h-11" data-testid="redeem-manual-check">{checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}</Button>
            </form>
          )}

          {checking && <div className="mt-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}

          {info && !checking && (info.valid ? (
            <div className="mt-5" data-testid="redeem-valid">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center">
                <div className="ff-cabinet text-lg font-bold text-emerald-800">{info.plan_name} plan · free forever</div>
                <div className="font-mono text-xs text-emerald-700">{code}</div>
              </div>
              <ul className="mt-4 space-y-2">
                {benefits.map((b) => (
                <li key={b} className="ff-manrope flex items-start gap-2 text-sm font-medium text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-ink" strokeWidth={2.2} /> {b}</li>
                ))}
              </ul>
              {loading ? null : isFounder ? (
              <Button onClick={redeem} disabled={busy} className="mt-6 w-full gap-1.5" data-testid="redeem-claim-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Claim {info.plan_name} free
                </Button>
              ) : user ? (
                <p className="mt-6 rounded-lg bg-slate-50 p-3 text-center text-sm text-slate-600" data-testid="redeem-wrong-role">
                  Access codes apply to founder workspaces. Sign out and use (or create) a founder account to claim this.
                </p>
              ) : (
                <div className="mt-6 space-y-2">
                <Button onClick={() => goAuth("/register?role=founder")} className="w-full gap-1.5" data-testid="redeem-signup-btn">Create a founder account <ArrowRight className="h-4 w-4" /></Button>
                  <Button onClick={() => goAuth("/login")} variant="outline" className="w-full" data-testid="redeem-login-btn">I already have an account</Button>
                </div>
              )}
            </div>
          ) : (
            <p className="mt-6 rounded-lg bg-red-50 p-3 text-center text-sm text-red-700" data-testid="redeem-invalid">
              {info.status === "redeemed" ? "This code has already been used." : info.status === "revoked" ? "This code is no longer active." : "That code isn't valid. Double-check it and try again."}
            </p>
          ))}
        </div>
        <p className="ff-manrope mt-4 text-center text-xs text-slate-500">Complimentary access provided by the TenX team.</p>
      </div>
    </div>
  );
}
